// API Services Index
// This file exports all API services for easy importing throughout the application

import authApiService from './AuthApiService';
import schedulerApiService from './SchedulerApiService';
import settingsApiService from './SettingsApiService';
import userApiService from './UserApiService';
import videoApiService from './VideoApiService';
import BaseApiService from './BaseApiService';

/**
 * API Services Collection
 * 
 * Centralized export of all API services for easy importing and usage
 * throughout the application.
 * 
 * @example
 * // Import specific services
 * import { authApi, schedulerApi } from '@/services/api';
 * 
 * // Or import all services
 * import * as apiServices from '@/services/api';
 * 
 * // Usage
 * const response = await authApi.authenticate(email, password);
 * const homeData = await schedulerApi.getHomeData();
 */

// Export individual service instances
export const authApi = authApiService;
export const schedulerApi = schedulerApiService;
export const settingsApi = settingsApiService;
export const userApi = userApiService;
export const videoApi = videoApiService;

// Export base service class for extension
export { BaseApiService };

// Export all services as a single object for convenience
export const apiServices = {
  auth: authApiService,
  scheduler: schedulerApiService,
  settings: settingsApiService,
  user: userApiService,
  video: videoApiService,
};

// Default export for backwards compatibility
export default apiServices;

/**
 * API Service Usage Examples:
 * 
 * // Authentication
 * import { authApi } from '@/services/api';
 * const loginResult = await authApi.authenticate('user@example.com', 'password');
 * 
 * // Scheduler operations
 * import { schedulerApi } from '@/services/api';
 * const homeData = await schedulerApi.getHomeData();
 * await schedulerApi.createNotification(notificationData);
 * 
 * // Settings management
 * import { settingsApi } from '@/services/api';
 * await settingsApi.updateLanguage('de');
 * await settingsApi.updatePassword(currentPassword, newPassword);
 * 
 * // User operations
 * import { userApi } from '@/services/api';
 * const profile = await userApi.getUserProfile();
 * const exerciseHistory = await userApi.getExerciseHistory();
 * 
 * // Using all services
 * import apiServices from '@/services/api';
 * const profile = await apiServices.user.getUserProfile();
 * const notifications = await apiServices.scheduler.getHomeData();
 */ 